import StoreOrderStepBadge from '@/dealers/components/StoreOrderStepBadge'
import Address from '@/gf/components/Address'
import Card from '@/gf/components/Card'
import Layout from '@/gf/components/Layout'
import Page from '@/gf/components/Page'
import useConfig from '@/gf/hooks/useConfig'
import useSearchCustomers from '@/gf/hooks/useSearchCustomers'
import Money from '@/gf/modules/Money'
import ProductM from '@/gf/modules/Product'
import StoreOrderM from '@/gf/modules/StoreOrder'
import Time from '@/gf/modules/Time'
import UserContext from '@/gf/modules/User'
import * as api from '@/suppliers/modules/api'
import { StoreOrdersSearch } from '@/suppliers/modules/api'
import nth from 'lodash/nth'
import pluralize from 'pluralize'
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { LineItem as LineItemT } from '../../types'
import AddressMetadata from '../components/AddressMetadata'
import Frame from '../components/Frame'
import useSession from '../hooks/useSession'
import QueryString from '../modules/QueryString'

const LineItem = ({ item }: { item: LineItemT }) => (
  <div className="flex space-x-4">
    <img
      src={ProductM.primaryImageURL(item.product, 'card')}
      alt={item.product.name}
      className="w-16 h-16 border rounded-sm overflow-hidden"
    />
    <div className="flex flex-1 flex-col">
      <Link to={`/products/${item.product.id}`} className="text-sm text-blue-500 underline">
        {item.product.name}
      </Link>
      <p className="text-sm text-gray-500">{`MPN: ${item.product.mpn}`}</p>
    </div>
    <p className="text-sm text-gray-700">
      {Money.format(item.discountedUnitPrice)} x {item.quantity}
    </p>
    <p className="text-sm text-gray-700">{Money.format(item.extendedPrice)}</p>
  </div>
)

const Customer = () => {
  const { id } = useParams<{ id: string }>() as { id: string }
  const { adminUrl } = useConfig()
  const [lastStoreOrder, setLastStoreOrder] = useState<StoreOrdersSearch['storeOrders'][number]>()
  const { store, user } = useSession()

  const filter = JSON.stringify([
    ['equals', 'id', id],
    ['equals', 'store_id', store?.id],
  ])
  const { data } = useSearchCustomers({ variables: { page: 1, filter } })
  const customer = data && (nth(data.customersSearch.customers, 0) || null)

  const breadcrumbs = {
    copy: 'Back to Dashboard',
    crumbs: [
      { name: 'Customers', href: '/customers' },
      { name: 'Customer details', href: `/customers/${id}` },
    ],
  }

  useEffect(() => {
    if (customer?.lastOrderId) {
      api.fetchStoreOrderForOrder(customer.lastOrderId, store?.id).then(setLastStoreOrder)
    }
  }, [customer?.lastOrderId, user])

  if (UserContext.isAdmin(user)) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p className="text-sm text-gray-700">
                Go to the{' '}
                <a href={`${adminUrl}/customers/${id}`} className="text-blue-500 underline">
                  admin customer page
                </a>
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  if (customer === null) {
    return (
      <Frame breadcrumbs={breadcrumbs}>
        <Page title="Oops">
          <Layout>
            <Layout.Section type="full">
              <p>Cannot find that customer.</p>
              <p>
                Please return to the{' '}
                <Link className="text-blue-500 underline" to="/customers/">
                  customers tab
                </Link>
                .
              </p>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    )
  }

  const pageTitle = customer ? customer.name : <>Loading&hellip;</>

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title={pageTitle}>
        {customer && (
          <Layout>
            <Layout.Section type="primary">
              <Card title="Latest Order">
                {lastStoreOrder ? (
                  <>
                    <Card.Section>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div className="col-span-2">
                          <Link
                            className="text-blue-500 underline text-sm"
                            to={`/orders/${lastStoreOrder.id}`}
                          >
                            {lastStoreOrder.shortId}
                          </Link>
                        </div>
                        <div className="col-span-1 text-right text-gray-500 text-sm">
                          {lastStoreOrder.insertedAt && Time.toString(lastStoreOrder.insertedAt)}
                        </div>
                        <div className="col-span-2 text-sm text-gray-700">
                          <StoreOrderStepBadge step={lastStoreOrder.step} />
                        </div>
                        <div className="col-span-1 text-right text-sm text-gray-700">
                          <p>{Money.format(StoreOrderM.total(lastStoreOrder))} total</p>
                        </div>
                      </div>
                    </Card.Section>
                    <Card.Section title={`Items (${lastStoreOrder.lineItems.length})`}>
                      {/* Only show the first two items in an order */}
                      {lastStoreOrder.lineItems.slice(0, 2).map((item, index) => (
                        <Card.SubSection key={item.id}>
                          <LineItem item={item} />
                          {/* Show ellipses if there are more line items */}
                          {index === 1 && lastStoreOrder.lineItems.length > 2 && (
                            <div className="mt-2">
                              <Link
                                className="text-gray-500 underline text-sm"
                                to={`/orders/${lastStoreOrder.id}`}
                              >
                                View all items
                              </Link>
                            </div>
                          )}
                        </Card.SubSection>
                      ))}
                    </Card.Section>
                  </>
                ) : (
                  <Card.Section>
                    <p>Loading&hellip;</p>
                  </Card.Section>
                )}
                <Card.Section>
                  <Link
                    className="text-blue-500 underline text-sm"
                    to={`/orders${QueryString.customerId(customer)}`}
                  >
                    View all orders
                  </Link>
                </Card.Section>
              </Card>
            </Layout.Section>
            <Layout.Section type="secondary">
              <Card title="Customer overview">
                <Card.Section>
                  <p className="text-sm text-gray-700">{customer.name}</p>
                  {customer.account && (
                    <p className="text-sm text-gray-700">{customer.account.name}</p>
                  )}
                  <p className="text-sm text-gray-700 mt-4">
                    {customer.ordersCount} {pluralize('order', customer.ordersCount)}
                  </p>
                </Card.Section>
                {customer.phoneNumber && (
                  <Card.Section title="Contact Information">
                    <p className="text-sm text-gray-700">{customer.phoneNumber}</p>
                  </Card.Section>
                )}
                {customer.shippingAddress && (
                  <Card.Section title="Shipping address">
                    <Address className="text-sm text-gray-700" address={customer.shippingAddress} />
                    {typeof customer.shippingAddress.deliverable === 'boolean' && (
                      <AddressMetadata className="mt-2" address={customer.shippingAddress} />
                    )}
                  </Card.Section>
                )}
                {customer.billingAddress && (
                  <Card.Section title="Billing address">
                    <Address className="text-sm text-gray-700" address={customer.billingAddress} />
                  </Card.Section>
                )}
              </Card>
            </Layout.Section>
          </Layout>
        )}
      </Page>
    </Frame>
  )
}

export default Customer
